import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Backdrop, Box, Button, Checkbox, CircularProgress, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Pagination from '../../../Common/Pagination/pagination';
import { Container, TableWrapper } from './ImportProfiles.styles';
import { useParams } from 'react-router-dom';
import { CloseStatus, OnHoldStatus, OpenStatus, StyledBox, TableDescription, TableRowStyled } from '../../JobManagement/JobListing/JobListing.styles';
import { uploadResumesToOcr } from './ImportProfilesApi';
import { useSnackbar } from '../../../../Context/SnackbarContext';
import { getJobs } from '../../JobManagement/jobManagemnetApi';
import SearchBar from '../../../Common/Search/SearchBar';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

interface JobData {
  JobNum: number;
  JobTitle: string;
  Department: string;
  Status: string;
  CreatedDate: string;
  CreatedBy: string;
  UpdatedDate: string;
  UpdatedBy: string;
  JobCode: string;
  year_of_experience?: number;
  ctc?: number;
  skills?: string[];
  recruiter?: string;
  [key: string]: any;
  DeletedItem: boolean;
  Skills: string[],
  JDId: string | null,
  UploadStatus: string | null
}


const ImportProfiles: React.FC = () => {
  const theme = useTheme();
  const [files, setFiles] = useState<File[]>([]);
  const { tenant } = useParams<{ tenant: string }>();
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalJobs, setTotalJobs] = useState(0)
  const [openjobData, setOpenJobData] = useState<JobData[]>([])
  const [selectedJob, setSelectedJob] = useState<number | null>(null);
  const columnsData = ['Job Posting ID', 'Job Title', 'Department', 'Job Status', 'Created Details', 'Updated Details']
  const [dataLoading, setDataLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstRender = useRef(true);
  const { showSnackbar } = useSnackbar();

  const fetchJobData = async (page: number, rowsPerPage: number, searchQuery: string) => {
    try {
      setDataLoading(true);
      const payLoad = {
        status: "Open",
        page: page,
        pageSize: rowsPerPage,
        jobTitle: searchQuery
      }
      const result = await getJobs(tenant, payLoad)
      if (result) {
        setOpenJobData(result.Jobs);
        setTotalJobs(result.TotalJobs);
      }

    } catch (error) {
      console.error('Error fetching job data:', error)
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    fetchJobData(page, rowsPerPage, searchQuery)
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; // 🚫 Skip on initial render
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchJobData(page, rowsPerPage, searchQuery);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery]);



  const formatEpochToDate = (epochTime: any) => {
    const date = new Date(epochTime * 1000); // Convert seconds to milliseconds
    return date.toISOString().split('T')[0]; // Format as 'YYYY-MM-DD'
  };


  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0 && selectedJob !== null) {
      const totalSize = acceptedFiles.reduce((acc, file) => acc + file.size, 0);
      const isValidType = acceptedFiles.every(file =>
        ['application/pdf', 'image/jpeg'].includes(file.type)
      );

      if (!isValidType) {
        showSnackbar('Only PDF and JPEG files are allowed.', 'error');
        return;
      }

      if (totalSize > 4 * 1024 * 1024) {
        showSnackbar('Total file size should be less than 4MB.', 'error');
        return;
      }

      setFiles(acceptedFiles); // Save all files
    }
  }, [selectedJob]);


  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: 4 * 1024 * 1024, // Optional: max size per file
    multiple: true,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
  });


  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleCheckboxChange = (jobId: number) => {
    if (selectedJob === jobId) {
      // Unselect if the same job is clicked again
      setSelectedJob(null);
    } else {
      setSelectedJob(jobId);
    }
  };



  const handleImport = async () => {
    if (files.length === 0 || !selectedJob) {
      showSnackbar("Please select at least one job and upload files.", "error");
      return
    }
    try {
      setLoading(true)
      const result = await uploadResumesToOcr(tenant, files, selectedJob.toString())
      if (result) {
        setFiles([])
        setSelectedJob(null)
        showSnackbar("Resumes processed successfully", "success");
      }
    } catch (error) {
      console.log(error)
      showSnackbar("Failed to process resumes. Please try again.", "error");
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container sx={{
      marginLeft: '2.3vw',
      marginRight: '1.3vw',
    }} >
      <Box >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: '1.66vw',
          }}
        >
          <Box>
            <Typography variant="h5" textAlign="left">
              Import Profiles
            </Typography>
            <Typography variant="subtitle1" textAlign="left" color="textSecondary">
              Upload and Associate Candidate Resumes with the Job Posting
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button variant="contained" color="primary" disabled={files.length === 0 || !selectedJob}
              onClick={handleImport} >
              <FileDownloadOutlinedIcon />  Import
            </Button>
          </Box>
        </Box>




        <Box
          {...getRootProps()}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          border={`2px dashed ${theme.palette.primary.main}`}
          borderRadius="8px"
          padding="2vh"
          height="25vh"
          sx={{
            width: '100%', // Ensuring it fills the parent width
            cursor: selectedJob !== null ? 'pointer' : 'not-allowed',
            backgroundColor: isDragActive
              ? theme.palette.action.hover
              : theme.palette.background.paper,
            opacity: selectedJob !== null ? 1 : 0.5, // Disable file selection if no checkboxes are selected
          }}
        >
          <input {...getInputProps()} disabled={selectedJob === null} />
          {files.length > 0 ? (<>
            <Box
              sx={{
                width: '100%',
                maxHeight: '100%',   // Restrict height inside upload box
                overflowY: 'auto',   // Enable vertical scroll
                paddingRight: '8px', // Optional: avoid scrollbar overlaying content
              }}
            >
              {files.map((file, index) => (
                <Box
                  key={index}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                  padding="0.5rem 1rem"
                  borderBottom="1px solid #ccc"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  overflow="hidden"
                >
                  <Box display="flex" flexDirection="column">
                    <Typography variant="subtitle1">File Name: {file.name}</Typography>
                    <Typography variant="subtitle1">
                      File Size: {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </Typography>
                  </Box>
                  <DeleteIcon
                    sx={{
                      fontSize: '4vh',
                      color: theme.palette.error.main,
                      cursor: 'pointer',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const updated = [...files];
                      updated.splice(index, 1);
                      setFiles(updated);
                    }}
                  />
                </Box>
              ))}
            </Box>
          </>) : (
            <>

              <CloudUploadIcon sx={{ fontSize: '4vh', color: theme.palette.primary.main }} />
              <Typography
                variant="caption"
                color="primary"
                sx={{ textDecoration: 'underline', cursor: selectedJob !== null ? 'pointer' : 'not-allowed' }}
              >
                Click to upload
              </Typography>
              <Typography variant="caption" color="textSecondary">
                or drag and drop
              </Typography>
              <Typography variant="caption" color="textSecondary">
                (Upload only <strong>PDF</strong> or <strong>JPEG</strong> files)
              </Typography>
              <Typography variant="caption" color="textSecondary">
                (Maximum total size: <strong>4MB</strong>)
              </Typography>
            </>)}
        </Box>


        {/* Instruction message: Add this to inform the user */}
        {selectedJob === null && (
          <Typography variant="body2" color="error" sx={{ marginTop: '1vw', textAlign: 'center' }}>
            Please select at least one job before uploading a file.
          </Typography>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            flexWrap: 'wrap',
            mb: '1.66vw',
            gap: 2,
            mt: '1.66vw',
          }}
        >

          <Typography variant="h5" sx={{ fontSize: '2.4vh', fontWeight: 'bold' }}>Open Jobs</Typography>


          <SearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            placeholder="Search by Job Title"
          />

        </Box>
      </Box>

      <Box mb='1.66vw'>
        <TableWrapper>
          <Box sx={{ overflowX: 'auto', width: '100%' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '5%' }}>
                    <Typography variant="caption">
                      Select
                    </Typography>
                  </TableCell>
                  {/* <TableCell sx={{ width: '5%' }} /> */}
                  {columnsData.map((column) => {
                    // Skip the first column (index 0) by checking the column name
                    return (
                      <TableCell
                        key={column}
                        sx={{
                          color: theme.palette.text.secondary,
                          padding: '8px 12px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '13.89vw',
                          minWidth: '6.94vw',
                        }}
                      >
                        <Typography variant="caption">
                          {column.charAt(0).toUpperCase() + column.slice(1).replace(/_/g, ' ')}
                        </Typography>
                      </TableCell>
                    );

                  })}
                </TableRow>

              </TableHead>
              <TableBody>
                {dataLoading ? (
                  <TableRow>
                    <TableCell colSpan={columnsData.length} style={{ textAlign: 'center', fontWeight: 'bold' }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) :
                  openjobData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columnsData.length} style={{ textAlign: 'center', fontWeight: 'bold' }}>
                        No Jobs Data Available
                      </TableCell>
                    </TableRow>
                  ) : (
                    openjobData.map((row, index) => (
                      <TableRowStyled key={row.JobNum} iseven={(index % 2 === 0).toString()}>
                        <TableCell sx={{ width: '5%' }}>
                          <Checkbox
                            checked={selectedJob === row.JobNum}
                            onChange={() => handleCheckboxChange(row.JobNum)}
                          />
                        </TableCell>
                        <TableDescription>{row.JobPostingId || '-'}</TableDescription>
                        <TableDescription>{row.JobTitle}</TableDescription>
                        <TableDescription>{row.Department}</TableDescription>
                        <TableDescription >
                          {row.Status === 'Open' ? (
                            <OpenStatus>• {row.Status}</OpenStatus>
                          ) : row.Status === 'Close' ? (
                            <CloseStatus>• {row.Status}</CloseStatus>
                          ) : (
                            <OnHoldStatus>• {row.Status}</OnHoldStatus>
                          )
                          }
                        </TableDescription>
                        <TableDescription>
                          <StyledBox >
                            <Typography variant='body2'>{row.CreatedBy}</Typography>
                            <Typography variant='body2'>{formatEpochToDate(row.CreatedDate)}</Typography>
                          </StyledBox>
                        </TableDescription>
                        <TableDescription>
                          <StyledBox >
                            <Typography variant='body2'>{row.UpdatedBy || '-'}</Typography>
                            <Typography variant='body2'>{row.UpdatedDate ? formatEpochToDate(row.UpdatedDate) : '-'}</Typography>
                          </StyledBox>
                        </TableDescription>
                      </TableRowStyled>
                    )))}

              </TableBody>
            </Table>
          </Box>

          <Box sx={{ mt: '2.5vh', display: 'flex', mb: 2, mx: 2, justifyContent: 'center' }}>
            <Pagination
              totalRows={totalJobs}
              rowsPerPage={rowsPerPage}
              currentPage={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </TableWrapper>
      </Box>
      {/* Circular Progress with Transparent Background */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column', // Stack the CircularProgress and Typography
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ marginTop: 2 }}>Please wait Uploading...</Typography>
      </Backdrop>
    </Container>
  );
};

export default ImportProfiles;
